import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateWebPageSchema } from '../../utils/seo';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // SEO Configuration
  useSEO({
    title: 'PAINTERRR - Find South Africa\'s Best House Painters | Professional Painting Services Cape Town',
    description: 'Connect with South Africa\'s most trusted professional house painters in Cape Town. Get free quotes from verified, certified painters for residential and commercial painting projects. Quality workmanship guaranteed.',
    keywords: 'house painters Cape Town, professional painters South Africa, residential painting, commercial painting, painting services, painters near me',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema({
          name: 'PAINTERRR - Find South Africa\'s Best House Painters',
          description: 'Connect with South Africa\'s most trusted professional house painters in Cape Town.',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com'
        }),
        generateLocalBusinessSchema({
          name: 'PAINTERRR',
          description: 'South Africa\'s premier directory for professional house painters',
          address: 'Cape Town',
          priceRange: '$$',
          serviceArea: ['Cape Town', 'Table View', 'Melkbosstrand', 'Durbanville', 'Bloubergstrand']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const painters = [
    {
      id: 1,
      name: "Painters Bloubergstrand",
      location: "Bloubergstrand",
      rating: 4.9,
      reviews: 127,
      image: "https://public.readdy.ai/ai/img_res/af826243e62a56be7a306a6df51e3b18.jpg",
      specialty: "Coastal Properties",
      experience: "15+ years",
      link: "/listing-painters-bloubergstrand",
      services: ["Exterior Painting", "Interior Painting", "Coastal Protection"],
      areas: "Bloubergstrand, Table View, West Beach, Big Bay"
    },
    {
      id: 2,
      name: "Painters Cape Town",
      location: "Cape Town CBD",
      rating: 4.8,
      reviews: 203,
      image: "https://readdy.ai/api/search-image?query=professional%20painter%20working%20on%20modern%20building%20exterior%20in%20urban%20setting%2C%20city%20backdrop%2C%20clean%20professional%20finish%2C%20bright%20daylight%2C%20commercial%20painting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=2&orientation=landscape",
      specialty: "Commercial & Residential",
      experience: "20+ years",
      link: "/listing-painters-cape-town",
      services: ["Commercial Painting", "Residential Painting", "Office Spaces"],
      areas: "Cape Town CBD, City Bowl, Foreshore, Woodstock"
    },
    {
      id: 3,
      name: "Painters Cape Town Main",
      location: "Cape Town",
      rating: 4.9,
      reviews: 245,
      image: "https://readdy.ai/api/search-image?query=professional%20painting%20crew%20working%20on%20large%20residential%20property%2C%20Cape%20Town%20cityscape%2C%20modern%20equipment%2C%20pristine%20finish%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=46&orientation=landscape",
      specialty: "Residential & Commercial",
      experience: "20+ years",
      link: "/listing-painters-cape-town-main",
      services: ["Interior Painting", "Exterior Painting", "Commercial Projects"],
      areas: "Cape Town, Southern Suburbs, Northern Suburbs, Atlantic Seaboard"
    },
    {
      id: 4,
      name: "Painters Western Cape",
      location: "Western Cape",
      rating: 4.8,
      reviews: 312,
      image: "https://readdy.ai/api/search-image?query=professional%20painters%20working%20on%20wine%20estate%20property%2C%20Western%20Cape%20landscape%2C%20mountain%20backdrop%2C%20elegant%20architecture%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=47&orientation=landscape",
      specialty: "Regional Coverage",
      experience: "18+ years",
      link: "/listing-painters-wc",
      services: ["Residential Painting", "Commercial Painting", "Estate Properties"],
      areas: "Cape Town, Stellenbosch, Paarl, Somerset West, George"
    },
    {
      id: 5,
      name: "Painting Contractors Cape Town",
      location: "Cape Town",
      rating: 5.0,
      reviews: 289,
      image: "https://readdy.ai/api/search-image?query=large-scale%20commercial%20painting%20project%2C%20industrial%20building%2C%20professional%20contractor%20team%2C%20modern%20equipment%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=48&orientation=landscape",
      specialty: "Commercial & Industrial",
      experience: "25+ years",
      link: "/listing-painting-contractors-cape-town",
      services: ["Commercial Painting", "Industrial Projects", "Large-Scale Contracts"],
      areas: "Cape Town, Bellville, Parow, Epping, Montague Gardens"
    },
    {
      id: 6,
      name: "Durbanville Painters Main",
      location: "Durbanville",
      rating: 4.9,
      reviews: 198,
      image: "https://readdy.ai/api/search-image?query=professional%20house%20painter%20working%20on%20family%20home%20exterior%2C%20Durbanville%20suburb%2C%20clean%20finish%2C%20bright%20sunny%20day%2C%20residential%20setting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=49&orientation=landscape",
      specialty: "House Painting",
      experience: "16+ years",
      link: "/listing-durbanville-painters-main",
      services: ["House Painting", "Interior Design", "Color Consultation"],
      areas: "Durbanville, Bellville, Brackenfell, Kraaifontein"
    },
    {
      id: 7,
      name: "Durbanville Painters Premium",
      location: "Durbanville",
      rating: 4.8,
      reviews: 176,
      image: "https://readdy.ai/api/search-image?query=premium%20home%20exterior%20painting%2C%20upscale%20Durbanville%20property%2C%20professional%20painter%20at%20work%2C%20elegant%20finish%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=50&orientation=landscape",
      specialty: "Premium Services",
      experience: "14+ years",
      link: "/listing-durbanville-painters-alt",
      services: ["Premium Finishes", "Residential Painting", "Commercial Projects"],
      areas: "Durbanville, Tygervalley, Bellville, Northern Suburbs"
    },
    {
      id: 8,
      name: "Painters Camps Bay",
      location: "Camps Bay",
      rating: 5.0,
      reviews: 89,
      image: "https://readdy.ai/api/search-image?query=luxury%20home%20exterior%20painting%20with%20mountain%20views%2C%20high-end%20property%2C%20pristine%20white%20walls%2C%20professional%20painter%20at%20work%2C%20elegant%20setting%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=3&orientation=landscape",
      specialty: "Luxury Homes",
      experience: "18+ years",
      link: "/listing-painters-camps-bay",
      services: ["Luxury Finishes", "High-End Estates", "Designer Colors"],
      areas: "Camps Bay, Clifton, Bantry Bay, Bakoven"
    },
    {
      id: 9,
      name: "Painters Constantia",
      location: "Constantia",
      rating: 4.9,
      reviews: 156,
      image: "https://readdy.ai/api/search-image?query=estate%20property%20exterior%20painting%2C%20large%20elegant%20home%2C%20professional%20painter%20working%2C%20lush%20garden%20setting%2C%20pristine%20finish%2C%20natural%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=4&orientation=landscape",
      specialty: "Estate Properties",
      experience: "22+ years",
      link: "/listing-painters-constantia",
      services: ["Estate Painting", "Heritage Homes", "Wine Estates"],
      areas: "Constantia, Bishopscourt, Tokai, Bergvliet"
    },
    {
      id: 10,
      name: "Painters Clifton",
      location: "Clifton",
      rating: 5.0,
      reviews: 94,
      image: "https://readdy.ai/api/search-image?query=beachfront%20luxury%20home%20painting%20modern%20architecture%20ocean%20views%2C%20professional%20painter%20at%20work%2C%20pristine%20white%20exterior%2C%20bright%20coastal%20setting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=5&orientation=landscape",
      specialty: "Beachfront Luxury",
      experience: "16+ years",
      link: "/listing-painters-clifton",
      services: ["Beachfront Homes", "Luxury Villas", "Modern Architecture"],
      areas: "Clifton, Camps Bay, Bantry Bay, Sea Point"
    },
    {
      id: 11,
      name: "Painters Table View",
      location: "Table View",
      rating: 4.8,
      reviews: 178,
      image: "https://readdy.ai/api/search-image?query=residential%20home%20exterior%20painting%2C%20suburban%20setting%2C%20professional%20painter%20working%2C%20clean%20walls%2C%20bright%20sunny%20day%2C%20neat%20finish%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=6&orientation=landscape",
      specialty: "Residential Painting",
      experience: "14+ years",
      link: "/listing-painters-table-view",
      services: ["Residential Painting", "Apartments", "Townhouses"],
      areas: "Table View, Parklands, Sunningdale, West Beach"
    },
    {
      id: 12,
      name: "Painters Durbanville",
      location: "Durbanville",
      rating: 4.9,
      reviews: 142,
      image: "https://readdy.ai/api/search-image?query=family%20home%20exterior%20painting%2C%20suburban%20neighborhood%2C%20professional%20painter%20at%20work%2C%20clean%20finish%2C%20bright%20daylight%2C%20residential%20setting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=7&orientation=landscape",
      specialty: "Family Homes",
      experience: "17+ years",
      link: "/listing-painters-durbanville",
      services: ["Family Homes", "New Builds", "Renovations"],
      areas: "Durbanville, Bellville, Brackenfell, Kraaifontein"
    },
    {
      id: 13,
      name: "Painters Century City",
      location: "Century City",
      rating: 4.7,
      reviews: 165,
      image: "https://readdy.ai/api/search-image?query=modern%20apartment%20building%20exterior%20painting%2C%20urban%20development%2C%20professional%20painters%20working%2C%20contemporary%20architecture%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=8&orientation=landscape",
      specialty: "Modern Apartments",
      experience: "12+ years",
      link: "/listing-painters-century-city",
      services: ["Apartment Complexes", "Body Corporate", "Modern Finishes"],
      areas: "Century City, Milnerton, Table View, Paarden Eiland"
    },
    {
      id: 14,
      name: "Painters Green Point",
      location: "Green Point",
      rating: 4.8,
      reviews: 134,
      image: "https://readdy.ai/api/search-image?query=urban%20apartment%20exterior%20painting%2C%20city%20living%2C%20professional%20painter%20working%2C%20modern%20building%2C%20bright%20natural%20light%2C%20clean%20finish%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=9&orientation=landscape",
      specialty: "Urban Living",
      experience: "15+ years",
      link: "/listing-painters-green-point",
      services: ["Urban Apartments", "City Living", "Modern Spaces"],
      areas: "Green Point, Sea Point, Mouille Point, V&A Waterfront"
    },
    {
      id: 15,
      name: "Painters Sea Point",
      location: "Sea Point",
      rating: 4.9,
      reviews: 198,
      image: "https://readdy.ai/api/search-image?query=coastal%20apartment%20building%20painting%2C%20ocean%20views%2C%20professional%20painters%20at%20work%2C%20modern%20exterior%2C%20bright%20coastal%20setting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=10&orientation=landscape",
      specialty: "Coastal Apartments",
      experience: "19+ years",
      link: "/listing-painters-sea-point",
      services: ["Coastal Apartments", "High-Rise Buildings", "Promenade Properties"],
      areas: "Sea Point, Green Point, Bantry Bay, Fresnaye"
    },
    {
      id: 16,
      name: "Painters Hout Bay",
      location: "Hout Bay",
      rating: 4.8,
      reviews: 112,
      image: "https://readdy.ai/api/search-image?query=harbor%20view%20home%20exterior%20painting%2C%20coastal%20village%20setting%2C%20professional%20painter%20working%2C%20scenic%20backdrop%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=11&orientation=landscape",
      specialty: "Harbor Properties",
      experience: "16+ years",
      link: "/listing-painters-hout-bay",
      services: ["Harbor Homes", "Coastal Properties", "Village Charm"],
      areas: "Hout Bay, Llandudno, Noordhoek, Chapman's Peak"
    },
    {
      id: 17,
      name: "Painters Rondebosch",
      location: "Rondebosch",
      rating: 4.7,
      reviews: 145,
      image: "https://readdy.ai/api/search-image?query=traditional%20home%20exterior%20painting%2C%20leafy%20suburb%2C%20professional%20painter%20at%20work%2C%20classic%20architecture%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=12&orientation=landscape",
      specialty: "Traditional Homes",
      experience: "21+ years",
      link: "/listing-painters-rondebosch",
      services: ["Traditional Homes", "Heritage Properties", "Classic Finishes"],
      areas: "Rondebosch, Newlands, Claremont, Mowbray"
    },
    {
      id: 18,
      name: "Painters Claremont",
      location: "Claremont",
      rating: 4.8,
      reviews: 167,
      image: "https://readdy.ai/api/search-image?query=suburban%20home%20exterior%20painting%2C%20established%20neighborhood%2C%20professional%20painter%20working%2C%20clean%20finish%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=13&orientation=landscape",
      specialty: "Suburban Homes",
      experience: "18+ years",
      link: "/listing-painters-claremont",
      services: ["Suburban Homes", "Family Properties", "Renovations"],
      areas: "Claremont, Kenilworth, Wynberg, Lansdowne"
    },
    {
      id: 19,
      name: "Painters Newlands",
      location: "Newlands",
      rating: 4.9,
      reviews: 129,
      image: "https://readdy.ai/api/search-image?query=elegant%20home%20exterior%20painting%2C%20upscale%20suburb%2C%20professional%20painter%20at%20work%2C%20mountain%20backdrop%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=14&orientation=landscape",
      specialty: "Upscale Properties",
      experience: "17+ years",
      link: "/listing-painters-newlands",
      services: ["Upscale Homes", "Garden Properties", "Premium Finishes"],
      areas: "Newlands, Rondebosch, Claremont, Bishopscourt"
    },
    {
      id: 20,
      name: "Painters Wynberg",
      location: "Wynberg",
      rating: 4.7,
      reviews: 153,
      image: "https://readdy.ai/api/search-image?query=heritage%20home%20exterior%20painting%2C%20historic%20suburb%2C%20professional%20painter%20working%2C%20classic%20architecture%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=15&orientation=landscape",
      specialty: "Heritage Homes",
      experience: "23+ years",
      link: "/listing-painters-wynberg",
      services: ["Heritage Restoration", "Victorian Homes", "Period Properties"],
      areas: "Wynberg, Plumstead, Kenilworth, Ottery"
    },
    {
      id: 21,
      name: "Painters Woodstock",
      location: "Woodstock",
      rating: 4.8,
      reviews: 186,
      image: "https://readdy.ai/api/search-image?query=industrial%20building%20exterior%20painting%2C%20urban%20creative%20district%2C%20professional%20painters%20working%2C%20modern%20finish%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=16&orientation=landscape",
      specialty: "Industrial & Creative",
      experience: "13+ years",
      link: "/listing-painters-woodstock",
      services: ["Industrial Spaces", "Creative Studios", "Urban Lofts"],
      areas: "Woodstock, Salt River, Observatory, Maitland"
    },
    {
      id: 22,
      name: "Painters Brackenfell",
      location: "Brackenfell",
      rating: 4.7,
      reviews: 141,
      image: "https://readdy.ai/api/search-image?query=modern%20family%20home%20exterior%20painting%2C%20new%20development%2C%20professional%20painter%20at%20work%2C%20clean%20finish%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=17&orientation=landscape",
      specialty: "New Developments",
      experience: "14+ years",
      link: "/listing-painters-brackenfell",
      services: ["New Developments", "Modern Homes", "Estate Living"],
      areas: "Brackenfell, Bellville, Durbanville, Kraaifontein"
    },
    {
      id: 23,
      name: "Painters Bellville",
      location: "Bellville",
      rating: 4.8,
      reviews: 172,
      image: "https://readdy.ai/api/search-image?query=commercial%20building%20exterior%20painting%2C%20business%20district%2C%20professional%20painters%20working%2C%20modern%20architecture%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=18&orientation=landscape",
      specialty: "Commercial Properties",
      experience: "19+ years",
      link: "/listing-painters-bellville-painters",
      services: ["Commercial Buildings", "Office Parks", "Retail Spaces"],
      areas: "Bellville, Parow, Goodwood, Tygervalley"
    },
    {
      id: 24,
      name: "Painters Parow",
      location: "Parow",
      rating: 4.6,
      reviews: 138,
      image: "https://readdy.ai/api/search-image?query=residential%20home%20exterior%20painting%2C%20established%20suburb%2C%20professional%20painter%20at%20work%2C%20neat%20finish%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=19&orientation=landscape",
      specialty: "Residential Services",
      experience: "16+ years",
      link: "/listing-painters-parow",
      services: ["Residential Painting", "Home Maintenance", "Renovations"],
      areas: "Parow, Bellville, Goodwood, Elsies River"
    },
    {
      id: 25,
      name: "Painters Goodwood",
      location: "Goodwood",
      rating: 4.7,
      reviews: 149,
      image: "https://readdy.ai/api/search-image?query=suburban%20home%20exterior%20painting%2C%20family%20neighborhood%2C%20professional%20painter%20at%20work%2C%20clean%20walls%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=20&orientation=landscape",
      specialty: "Family Homes",
      experience: "15+ years",
      link: "/listing-painters-goodwood",
      services: ["Family Homes", "Suburban Properties", "Interior & Exterior"],
      areas: "Goodwood, Parow, Bellville, Edgemead"
    },
    {
      id: 26,
      name: "Pro Painters Milnerton",
      location: "Milnerton",
      rating: 4.9,
      reviews: 191,
      image: "https://readdy.ai/api/search-image?query=waterfront%20property%20exterior%20painting%2C%20canal%20views%2C%20professional%20painter%20working%2C%20modern%20home%2C%20bright%20coastal%20setting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=21&orientation=landscape",
      specialty: "Waterfront Properties",
      experience: "20+ years",
      link: "/listing-painters-pro-painters-milnerton",
      services: ["Waterfront Homes", "Canal Properties", "Modern Living"],
      areas: "Milnerton, Century City, Table View, Lagoon Beach"
    },
    {
      id: 27,
      name: "Painters Parklands",
      location: "Parklands",
      rating: 4.8,
      reviews: 163,
      image: "https://readdy.ai/api/search-image?query=modern%20townhouse%20exterior%20painting%2C%20gated%20community%2C%20professional%20painter%20at%20work%2C%20contemporary%20design%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=22&orientation=landscape",
      specialty: "Gated Communities",
      experience: "13+ years",
      link: "/listing-painters-parklands",
      services: ["Gated Communities", "Townhouses", "Security Estates"],
      areas: "Parklands, Table View, Sunningdale, West Beach"
    },
    {
      id: 28,
      name: "Painters Big Bay",
      location: "Big Bay",
      rating: 4.7,
      reviews: 125,
      image: "https://readdy.ai/api/search-image?query=beachfront%20home%20exterior%20painting%2C%20ocean%20views%2C%20professional%20painter%20working%2C%20coastal%20property%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=23&orientation=landscape",
      specialty: "Beachfront Homes",
      experience: "12+ years",
      link: "/listing-painters-big-bay",
      services: ["Beachfront Properties", "Coastal Homes", "Ocean Views"],
      areas: "Big Bay, Bloubergstrand, Table View, West Beach"
    },
    {
      id: 29,
      name: "Painters Melkbosstrand",
      location: "Melkbosstrand",
      rating: 4.8,
      reviews: 147,
      image: "https://readdy.ai/api/search-image?query=coastal%20home%20exterior%20painting%2C%20beach%20setting%2C%20professional%20painter%20at%20work%2C%20clean%20finish%2C%20bright%20coastal%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=24&orientation=landscape",
      specialty: "Coastal Properties",
      experience: "14+ years",
      link: "/listing-painters-melkbosstrand",
      services: ["Coastal Homes", "Beach Properties", "Salt Protection"],
      areas: "Melkbosstrand, Atlantis, West Coast, Duynefontein"
    },
    {
      id: 30,
      name: "Painters Somerset West",
      location: "Somerset West",
      rating: 4.9,
      reviews: 184,
      image: "https://readdy.ai/api/search-image?query=wine%20country%20home%20exterior%20painting%2C%20mountain%20views%2C%20professional%20painter%20at%20work%2C%20elegant%20property%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=25&orientation=landscape",
      specialty: "Wine Country Estates",
      experience: "21+ years",
      link: "/listing-painters-somerset-west",
      services: ["Wine Estates", "Mountain Properties", "Luxury Homes"],
      areas: "Somerset West, Strand, Gordon's Bay, Helderberg"
    },
    {
      id: 31,
      name: "Painters Paarl",
      location: "Paarl",
      rating: 4.7,
      reviews: 136,
      image: "https://readdy.ai/api/search-image?query=vineyard%20estate%20exterior%20painting%2C%20wine%20region%2C%20professional%20painter%20at%20work%2C%20elegant%20architecture%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=26&orientation=landscape",
      specialty: "Vineyard Estates",
      experience: "18+ years",
      link: "/listing-painters-paarl",
      services: ["Vineyard Properties", "Wine Estates", "Country Homes"],
      areas: "Paarl, Wellington, Franschhoek, Stellenbosch"
    },
    {
      id: 32,
      name: "Painters George",
      location: "George",
      rating: 4.8,
      reviews: 159,
      image: "https://readdy.ai/api/search-image?query=garden%20route%20home%20exterior%20painting%2C%20lush%20setting%2C%20professional%20painter%20at%20work%2C%20beautiful%20property%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=27&orientation=landscape",
      specialty: "Garden Route Properties",
      experience: "17+ years",
      link: "/listing-painters-george",
      services: ["Garden Route Homes", "Coastal Properties", "Holiday Homes"],
      areas: "George, Wilderness, Knysna, Mossel Bay"
    },
    {
      id: 33,
      name: "Painters Hermanus",
      location: "Hermanus",
      rating: 4.9,
      reviews: 143,
      image: "https://readdy.ai/api/search-image?query=whale%20coast%20home%20exterior%20painting%2C%20ocean%20views%2C%20professional%20painter%20at%20work%2C%20coastal%20elegance%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=28&orientation=landscape",
      specialty: "Whale Coast Properties",
      experience: "16+ years",
      link: "/listing-painters-hermanus",
      services: ["Coastal Homes", "Holiday Properties", "Ocean Views"],
      areas: "Hermanus, Gansbaai, Stanford, Kleinmond"
    },
    {
      id: 34,
      name: "Painters Bantry Bay",
      location: "Bantry Bay",
      rating: 5.0,
      reviews: 87,
      image: "https://readdy.ai/api/search-image?query=luxury%20clifftop%20home%20exterior%20painting%2C%20ocean%20panorama%2C%20professional%20painter%20working%2C%20high-end%20property%2C%20bright%20coastal%20setting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=29&orientation=landscape",
      specialty: "Clifftop Luxury",
      experience: "19+ years",
      link: "/listing-painters-bantry-bay",
      services: ["Clifftop Homes", "Luxury Properties", "Ocean Panoramas"],
      areas: "Bantry Bay, Clifton, Camps Bay, Fresnaye"
    },
    {
      id: 35,
      name: "Painters Tamboerskloof",
      location: "Tamboerskloof",
      rating: 4.8,
      reviews: 152,
      image: "https://readdy.ai/api/search-image?query=city%20bowl%20home%20exterior%20painting%2C%20mountain%20slopes%2C%20professional%20painter%20at%20work%2C%20urban%20elegance%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=30&orientation=landscape",
      specialty: "City Bowl Properties",
      experience: "15+ years",
      link: "/listing-painters-tamboerskloof",
      services: ["City Bowl Homes", "Mountain Properties", "Urban Living"],
      areas: "Tamboerskloof, Gardens, Oranjezicht, Higgovale"
    },
    {
      id: 36,
      name: "Painters Bishopscourt",
      location: "Bishopscourt",
      rating: 5.0,
      reviews: 76,
      image: "https://readdy.ai/api/search-image?query=exclusive%20estate%20exterior%20painting%2C%20luxury%20mansion%2C%20professional%20painter%20working%2C%20pristine%20grounds%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=31&orientation=landscape",
      specialty: "Exclusive Estates",
      experience: "24+ years",
      link: "/listing-painters-bishopscourt",
      services: ["Exclusive Estates", "Luxury Mansions", "Premium Properties"],
      areas: "Bishopscourt, Constantia, Kirstenbosch, Wynberg"
    },
    {
      id: 37,
      name: "Painters Tokai",
      location: "Tokai",
      rating: 4.7,
      reviews: 168,
      image: "https://readdy.ai/api/search-image?query=forest%20edge%20home%20exterior%20painting%2C%20nature%20setting%2C%20professional%20painter%20at%20work%2C%20peaceful%20property%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=32&orientation=landscape",
      specialty: "Forest Edge Properties",
      experience: "16+ years",
      link: "/listing-painters-tokai",
      services: ["Forest Properties", "Nature Settings", "Tranquil Homes"],
      areas: "Tokai, Constantia, Bergvliet, Steenberg"
    },
    {
      id: 38,
      name: "Painters Kenilworth",
      location: "Kenilworth",
      rating: 4.8,
      reviews: 144,
      image: "https://readdy.ai/api/search-image?query=suburban%20home%20exterior%20painting%2C%20leafy%20streets%2C%20professional%20painter%20at%20work%2C%20family%20property%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=33&orientation=landscape",
      specialty: "Suburban Homes",
      experience: "17+ years",
      link: "/listing-painters-kenilworth",
      services: ["Suburban Properties", "Family Homes", "Leafy Suburbs"],
      areas: "Kenilworth, Claremont, Wynberg, Plumstead"
    },
    {
      id: 39,
      name: "Painters Plattekloof",
      location: "Plattekloof",
      rating: 4.7,
      reviews: 157,
      image: "https://readdy.ai/api/search-image?query=modern%20estate%20home%20exterior%20painting%2C%20security%20estate%2C%20professional%20painter%20at%20work%2C%20contemporary%20design%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=34&orientation=landscape",
      specialty: "Security Estates",
      experience: "13+ years",
      link: "/listing-painters-plattekloof",
      services: ["Security Estates", "Modern Homes", "Gated Living"],
      areas: "Plattekloof, Panorama, Durbanville, Bellville"
    },
    {
      id: 40,
      name: "Painters Edgemead",
      location: "Edgemead",
      rating: 4.6,
      reviews: 133,
      image: "https://readdy.ai/api/search-image?query=family%20home%20exterior%20painting%2C%20residential%20suburb%2C%20professional%20painter%20at%20work%2C%20neat%20property%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=35&orientation=landscape",
      specialty: "Family Residences",
      experience: "14+ years",
      link: "/listing-painters-edgemead",
      services: ["Family Homes", "Residential Properties", "Suburban Living"],
      areas: "Edgemead, Bothasig, Goodwood, Monte Vista"
    },
    {
      id: 41,
      name: "Painters Bothasig",
      location: "Bothasig",
      rating: 4.7,
      reviews: 146,
      image: "https://readdy.ai/api/search-image?query=residential%20home%20exterior%20painting%2C%20quiet%20suburb%2C%20professional%20painter%20at%20work%2C%20clean%20finish%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=36&orientation=landscape",
      specialty: "Residential Painting",
      experience: "15+ years",
      link: "/listing-painters-bothasig",
      services: ["Residential Homes", "Suburban Properties", "Family Living"],
      areas: "Bothasig, Edgemead, Goodwood, Parow"
    },
    {
      id: 42,
      name: "Painters Sunningdale",
      location: "Sunningdale",
      rating: 4.8,
      reviews: 139,
      image: "https://readdy.ai/api/search-image?query=golf%20estate%20home%20exterior%20painting%2C%20fairway%20views%2C%20professional%20painter%20at%20work%2C%20upscale%20property%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=37&orientation=landscape",
      specialty: "Golf Estate Homes",
      experience: "16+ years",
      link: "/listing-painters-sunningdale",
      services: ["Golf Estates", "Upscale Homes", "Fairway Properties"],
      areas: "Sunningdale, Table View, Parklands, West Beach"
    },
    {
      id: 43,
      name: "Painters Malmesbury",
      location: "Malmesbury",
      rating: 4.6,
      reviews: 121,
      image: "https://readdy.ai/api/search-image?query=country%20home%20exterior%20painting%2C%20rural%20setting%2C%20professional%20painter%20at%20work%2C%20farmhouse%20style%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=38&orientation=landscape",
      specialty: "Country Properties",
      experience: "18+ years",
      link: "/listing-painters-malmesbury",
      services: ["Country Homes", "Farmhouses", "Rural Properties"],
      areas: "Malmesbury, Darling, Moorreesburg, Riebeek Kasteel"
    },
    {
      id: 44,
      name: "Plumstead Home Painters",
      location: "Plumstead",
      rating: 4.8,
      reviews: 174,
      image: "https://readdy.ai/api/search-image?query=traditional%20suburban%20home%20exterior%20painting%2C%20tree-lined%20street%2C%20professional%20painter%20at%20work%2C%20classic%20architecture%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=39&orientation=landscape",
      specialty: "Residential & Commercial",
      experience: "15+ years",
      link: "/listing-painters-plumstead",
      services: ["Residential Painting", "Commercial Spaces", "Renovations"],
      areas: "Plumstead, Wynberg, Diep River, Southfield"
    },
    {
      id: 45,
      name: "Kirstenhof Professional Painters",
      location: "Kirstenhof",
      rating: 4.9,
      reviews: 162,
      image: "https://readdy.ai/api/search-image?query=upscale%20suburban%20home%20exterior%20painting%2C%20mountain%20backdrop%2C%20professional%20painter%20at%20work%2C%20elegant%20property%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=40&orientation=landscape",
      specialty: "Estate & Interior Design",
      experience: "16+ years",
      link: "/listing-painters-kirstenhof",
      services: ["Estate Painting", "Interior Design", "Premium Finishes"],
      areas: "Kirstenhof, Constantia, Tokai, Bergvliet"
    },
    {
      id: 46,
      name: "Vredehoek City Painters",
      location: "Vredehoek",
      rating: 4.8,
      reviews: 188,
      image: "https://readdy.ai/api/search-image?query=urban%20hillside%20apartment%20exterior%20painting%2C%20city%20views%2C%20professional%20painter%20working%2C%20modern%20building%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=41&orientation=landscape",
      specialty: "Apartments & Commercial",
      experience: "14+ years",
      link: "/listing-painters-vredehoek",
      services: ["Apartment Painting", "Commercial Spaces", "City Living"],
      areas: "Vredehoek, Gardens, Tamboerskloof, Oranjezicht"
    },
    {
      id: 47,
      name: "Painters Fish Hoek",
      location: "Fish Hoek",
      rating: 4.7,
      reviews: 155,
      image: "https://readdy.ai/api/search-image?query=beachfront%20coastal%20home%20exterior%20painting%2C%20ocean%20views%2C%20professional%20painter%20at%20work%2C%20marine-grade%20coating%2C%20bright%20coastal%20setting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=42&orientation=landscape",
      specialty: "Coastal & Marine-Grade",
      experience: "14+ years",
      link: "/listing-painters-fish-hoek",
      services: ["Coastal Painting", "Marine-Grade Coatings", "Salt Damage Repair"],
      areas: "Fish Hoek, Clovelly, Sunny Cove, Glencairn"
    },
    {
      id: 48,
      name: "Painters Simon's Town",
      location: "Simon's Town",
      rating: 4.9,
      reviews: 141,
      image: "https://readdy.ai/api/search-image?query=heritage%20victorian%20home%20exterior%20painting%2C%20naval%20town%2C%20professional%20painter%20working%2C%20period%20architecture%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=43&orientation=landscape",
      specialty: "Heritage & Naval Housing",
      experience: "15+ years",
      link: "/listing-painters-simons-town",
      services: ["Heritage Restoration", "Naval Housing", "Victorian Homes"],
      areas: "Simon's Town, Boulders, Red Hill, Glencairn"
    },
    {
      id: 49,
      name: "Painters West Beach",
      location: "West Beach",
      rating: 4.6,
      reviews: 128,
      image: "https://readdy.ai/api/search-image?query=beachfront%20property%20exterior%20painting%2C%20windy%20coastal%20setting%2C%20professional%20painter%20at%20work%2C%20wind-resistant%20coating%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=44&orientation=landscape",
      specialty: "Beachfront & Wind-Resistant",
      experience: "13+ years",
      link: "/listing-painters-west-beach",
      services: ["Beachfront Painting", "Wind-Resistant Coatings", "Sand Protection"],
      areas: "West Beach, Bloubergstrand, Table View, Big Bay"
    },
    {
      id: 50,
      name: "Painters Muizenberg",
      location: "Muizenberg",
      rating: 4.8,
      reviews: 179,
      image: "https://readdy.ai/api/search-image?query=colorful%20beach%20cottage%20exterior%20painting%2C%20vibrant%20coastal%20colors%2C%20professional%20painter%20at%20work%2C%20beach%20setting%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=45&orientation=landscape",
      specialty: "Beach Cottages & Apartments",
      experience: "14+ years",
      link: "/listing-painters-muizenberg",
      services: ["Beach Cottage Painting", "Apartment Painting", "Commercial Painting"],
      areas: "Muizenberg, St James, Kalk Bay, Fish Hoek"
    },
    {
      id: 51,
      name: "Painters Johannesburg",
      location: "Johannesburg",
      rating: 4.9,
      reviews: 267,
      image: "https://readdy.ai/api/search-image?query=modern%20urban%20home%20exterior%20painting%2C%20Johannesburg%20skyline%2C%20professional%20painter%20at%20work%2C%20contemporary%20architecture%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=51&orientation=landscape",
      specialty: "Urban Residential & Commercial",
      experience: "22+ years",
      link: "/listing-painters-johannesburg",
      services: ["Residential Painting", "Commercial Projects", "High-Rise Buildings"],
      areas: "Johannesburg, Sandton, Rosebank, Midrand, Randburg"
    },
    {
      id: 52,
      name: "Painters JHB",
      location: "Johannesburg",
      rating: 4.8,
      reviews: 234,
      image: "https://readdy.ai/api/search-image?query=suburban%20estate%20home%20exterior%20painting%2C%20Johannesburg%20suburbs%2C%20professional%20painter%20working%2C%20elegant%20property%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=52&orientation=landscape",
      specialty: "Suburban Estates & Offices",
      experience: "19+ years",
      link: "/listing-painters-jhb",
      services: ["Estate Painting", "Office Buildings", "Townhouse Complexes"],
      areas: "Sandton, Fourways, Bryanston, Roodepoort, Centurion"
    },
    {
      id: 53,
      name: "Painters Noordhoek",
      location: "Noordhoek",
      rating: 4.7,
      reviews: 118,
      image: "https://readdy.ai/api/search-image?query=coastal%20village%20home%20exterior%20painting%2C%20mountain%20backdrop%2C%20professional%20painter%20at%20work%2C%20rustic%20charm%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=53&orientation=landscape",
      specialty: "Coastal Village Homes",
      experience: "16+ years",
      link: "/listing-painters-noordhoek",
      services: ["Village Homes", "Beach Properties", "Equestrian Estates"],
      areas: "Noordhoek, Kommetjie, Sun Valley, Chapman's Peak"
    },
    {
      id: 54,
      name: "Painters De Waterkant",
      location: "De Waterkant",
      rating: 4.9,
      reviews: 195,
      image: "https://readdy.ai/api/search-image?query=trendy%20urban%20apartment%20exterior%20painting%2C%20vibrant%20neighborhood%2C%20professional%20painter%20working%2C%20modern%20design%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=54&orientation=landscape",
      specialty: "Urban Apartments & Lofts",
      experience: "14+ years",
      link: "/listing-painters-de-waterkant",
      services: ["Apartment Painting", "Loft Conversions", "Modern Interiors"],
      areas: "De Waterkant, Green Point, V&A Waterfront, Cape Quarter"
    },
    {
      id: 55,
      name: "Painters Sandown",
      location: "Sandown",
      rating: 4.9,
      reviews: 156,
      image: "https://readdy.ai/api/search-image?query=luxury%20golf%20estate%20home%20exterior%20painting%2C%20pristine%20property%2C%20professional%20painter%20at%20work%2C%20upscale%20setting%2C%20bright%20natural%20light%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=55&orientation=landscape",
      specialty: "Luxury Homes & Estates",
      experience: "17+ years",
      link: "/listing-painters-sandown",
      services: ["Estate Painting", "Luxury Homes", "Golf Estate Properties"],
      areas: "Sandown, Bloubergstrand, Table View, Milnerton"
    },
    {
      id: 56,
      name: "Painters Kalk Bay",
      location: "Kalk Bay",
      rating: 4.8,
      reviews: 143,
      image: "https://readdy.ai/api/search-image?query=coastal%20heritage%20home%20exterior%20painting%2C%20harbor%20village%2C%20professional%20painter%20working%2C%20charming%20architecture%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=56&orientation=landscape",
      specialty: "Coastal & Heritage Homes",
      experience: "19+ years",
      link: "/listing-painters-kalk-bay",
      services: ["Heritage Restoration", "Coastal Properties", "Victorian Homes"],
      areas: "Kalk Bay, St James, Muizenberg, Fish Hoek"
    },
    {
      id: 57,
      name: "Painters Royal Ascot",
      location: "Royal Ascot",
      rating: 5.0,
      reviews: 128,
      image: "https://readdy.ai/api/search-image?query=exclusive%20golf%20estate%20mansion%20exterior%20painting%2C%20fairway%20views%2C%20professional%20painter%20at%20work%2C%20luxury%20property%2C%20bright%20sunny%20day%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=57&orientation=landscape",
      specialty: "Golf Estate Luxury",
      experience: "21+ years",
      link: "/listing-painters-royal-ascot",
      services: ["Golf Estate Homes", "Luxury Properties", "Premium Finishes"],
      areas: "Royal Ascot, Milnerton, Table View, Century City"
    },
    {
      id: 58,
      name: "Painters Tygerberg",
      location: "Tygerberg",
      rating: 4.8,
      reviews: 189,
      image: "https://readdy.ai/api/search-image?query=modern%20commercial%20building%20exterior%20painting%2C%20business%20district%2C%20professional%20painters%20working%2C%20contemporary%20architecture%2C%20bright%20daylight%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=58&orientation=landscape",
      specialty: "Residential & Commercial",
      experience: "18+ years",
      link: "/listing-painters-tygerberg",
      services: ["Commercial Painting", "Residential Projects", "Medical Facilities"],
      areas: "Tygerberg, Bellville, Parow, Goodwood"
    },
    {
      id: 59,
      name: "Painters Kommetjie",
      location: "Kommetjie",
      rating: 4.9,
      reviews: 134,
      image: "https://readdy.ai/api/search-image?query=coastal%20beach%20house%20exterior%20painting%2C%20ocean%20views%2C%20professional%20painter%20at%20work%2C%20seaside%20property%2C%20bright%20coastal%20setting%2C%20high%20quality%2C%20detailed%2C%20realistic&width=400&height=300&seq=59&orientation=landscape",
      specialty: "Coastal & Beach Houses",
      experience: "20+ years",
      link: "/listing-painters-kommetjie",
      services: ["Beach Houses", "Coastal Properties", "Salt Protection"],
      areas: "Kommetjie, Noordhoek, Ocean View, Scarborough"
    }
  ];

  // Filter painters based on search query
  const filteredPainters = painters.filter(painter => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      painter.name.toLowerCase().includes(query) ||
      painter.location.toLowerCase().includes(query) ||
      painter.specialty.toLowerCase().includes(query) ||
      painter.areas.toLowerCase().includes(query) ||
      painter.services.some(service => service.toLowerCase().includes(query))
    );
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Scroll to listings section
    const listingsSection = document.getElementById('listings');
    if (listingsSection) {
      listingsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-[#e9f638] rounded-lg flex items-center justify-center">
                <i className="ri-paint-brush-fill text-2xl text-black"></i>
              </div>
              <span className="text-2xl font-bold text-black">Painterrr</span>
            </div>
            <div className="flex items-center space-x-6">
              <a href="#listings" className={`${isScrolled ? 'text-gray-700' : 'text-white'} hover:text-[#e9f638] transition-colors whitespace-nowrap`}>
                Find Painters
              </a>
              <Link to="/about" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">
                About
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap">
                Contact
              </Link>
              <Link
                to="/get-a-quote"
                className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Get a Quote
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-black text-white overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://d64gsuwffb70l.cloudfront.net/691eb692e95f9e20469a7cda_1763620582281_831eeca4.webp" 
            alt="Professional house painting" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
        </div>
        <div className="relative container mx-auto px-4 py-24 md:py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Find South Africa's
              <span className="text-[#e9f638]"> Best House Painters</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              Connect with professional, certified painters in your area. Quality workmanship, competitive pricing, and exceptional results guaranteed.
            </p>
            <form className="mb-8" onSubmit={handleSearch}>
              <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
                <input 
                  type="text"
                  placeholder="Search by location (e.g., Table View, Melkbosstrand, Durbanville)" 
                  className="flex-1 px-6 py-4 rounded-lg text-black text-lg focus:outline-none focus:ring-2 focus:ring-[#e9f638]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button 
                  type="submit"
                  className="bg-[#e9f638] text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-white transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Search
                </button>
              </div>
            </form>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/get-a-quote" 
                className="bg-[#e9f638] text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-white transition-colors duration-300 text-center whitespace-nowrap cursor-pointer"
              >
                Get Free Quotes
              </Link>
              <a 
                href="#listings" 
                className="bg-transparent border-2 border-[#e9f638] text-[#e9f638] px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#e9f638] hover:text-black transition-colors duration-300 text-center whitespace-nowrap cursor-pointer"
              >
                Browse Painters
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Why Use <span className="text-[#e9f638]">Painterrr</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            We connect you with South Africa's most trusted painting professionals, making your search for the perfect painter simple and stress-free.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-[#e9f638] cursor-pointer">
              <h3 className="text-xl font-bold mb-3">Verified Professionals</h3>
              <p className="text-gray-600">All painters in our directory are thoroughly vetted, certified, and insured for your peace of mind.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-[#e9f638] cursor-pointer">
              <h3 className="text-xl font-bold mb-3">Local Expertise</h3>
              <p className="text-gray-600">Find painters who understand South African homes, climate conditions, and architectural styles.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-[#e9f638] cursor-pointer">
              <h3 className="text-xl font-bold mb-3">Competitive Quotes</h3>
              <p className="text-gray-600">Compare multiple quotes from top-rated painters in your area and choose the best fit for your budget.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-[#e9f638] cursor-pointer">
              <h3 className="text-xl font-bold mb-3">Quality Guaranteed</h3>
              <p className="text-gray-600">Our featured painters stand behind their work with comprehensive warranties and satisfaction guarantees.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-[#e9f638] cursor-pointer">
              <h3 className="text-xl font-bold mb-3">Free Consultation</h3>
              <p className="text-gray-600">Get expert color advice and project planning assistance at no cost from experienced professionals.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg hover:shadow-lg transition-shadow duration-300 border-2 border-transparent hover:border-[#e9f638] cursor-pointer">
              <h3 className="text-xl font-bold mb-3">Fast Response</h3>
              <p className="text-gray-600">Connect with available painters quickly and get your project started without unnecessary delays.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Painters Section */}
      <section id="listings" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Featured <span className="text-[#e9f638]">Painters</span>
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Discover South Africa's most trusted painting professionals. Each painter has been vetted for quality, reliability, and customer satisfaction.
          </p>
          
          {filteredPainters.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600 mb-4">No painters found matching "{searchQuery}"</p>
              <button 
                onClick={() => setSearchQuery('')}
                className="bg-[#e9f638] text-black px-6 py-3 rounded-lg font-semibold hover:bg-black hover:text-[#e9f638] transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <>
              {searchQuery && (
                <div className="mb-6 text-center">
                  <p className="text-gray-600">
                    Showing {filteredPainters.length} painter{filteredPainters.length !== 1 ? 's' : ''} for "{searchQuery}"
                    <button 
                      onClick={() => setSearchQuery('')}
                      className="ml-4 text-[#e9f638] hover:underline cursor-pointer"
                    >
                      Clear
                    </button>
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPainters.map((painter, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 border-2 border-transparent hover:border-[#e9f638]"
                  >
                    <div className="h-64 w-full overflow-hidden">
                      <img 
                        src={painter.image} 
                        alt={painter.name}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-black mb-2">{painter.name}</h3>
                      <p className="text-gray-600 mb-4">{painter.location}</p>
                      <div className="mb-4">
                        <h4 className="font-semibold text-black mb-2">Services:</h4>
                        <div className="flex flex-wrap gap-2">
                          {painter.services.map((service, idx) => (
                            <span key={idx} className="bg-black text-white px-3 py-1 rounded-full text-sm whitespace-nowrap">
                              {service}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="mb-6">
                        <h4 className="font-semibold text-black mb-2">Service Areas:</h4>
                        <p className="text-gray-600 text-sm">{painter.areas}</p>
                      </div>
                      <div className="flex gap-3">
                        <Link 
                          to={painter.link}
                          className="flex-1 bg-black text-white text-center py-3 rounded-lg font-bold hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                        >
                          View Details
                        </Link>
                        <Link 
                          to="/contact"
                          className="flex-1 bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
                        >
                          Get Quote
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">
            What Our <span className="text-[#e9f638]">Clients Say</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#e9f638]"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "Great service, was impressed by the quick response. Had 3 companies reach out to me for quotes within 1 business day!"
              </p>
              <p className="font-semibold">- Cheryl</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#e9f638]"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "The team was very helpful and professional. Great service - I received a phone call immediately, and they're already working on a quote."
              </p>
              <p className="font-semibold">- Keith</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#e9f638]"></i>
                ))}
              </div>
              <p className="text-gray-700 mb-4">
                "Great service, knowledgeable service providers contacted me within 24 hours."
              </p>
              <p className="font-semibold">- Tracey</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#e9f638]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-gray-800 mb-8 max-w-2xl mx-auto">
            Get connected with South Africa's best professional painters today. Free quotes, no obligation.
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-black text-white px-12 py-4 rounded-lg font-bold text-lg hover:bg-gray-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Get Your Free Quote Now
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by leaderr
              </a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                SEO by SEO Studio
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
